import React, { useCallback, useEffect, useMemo, useState } from "react";
import { COTIZACIONES_API, getComparativo } from "./cotizacionesApi";
import "./cotizaciones-v2.css";

const TABS = [
  { key: "comparativo", label: "Comparativo" },
  { key: "componentes", label: "Por componente" },
  { key: "combinada", label: "Compra combinada" },
  { key: "usa", label: "Mercado USA" },
  { key: "notas", label: "Notas ejecutivas" },
];

const LICENSE_SCENARIO = {
  currentProvider: "Prosoft 2000",
  currentUnitCost: 29.9976,
  alternativeProvider: "Image China",
  alternativeUnitCost: 12.22,
};

const EXEC_NOTES = [
  "Cerrar cotizaciones incompletas: laminador, envío e IVA.",
  "CardCube gana en PVC; Image China es más completo.",
  "Separar compra por insumo puede bajar costo.",
  "Todos los costos son en Pesos Mexicanos.",
  "Hologram Master: 42,372.50 MXN.",
];

const EXEC_CONTEXT = [
  "Se contactó a 10 proveedores en China; únicamente uno cuenta con la capacidad de suministrar el kit completo de insumos y equipos necesarios para la emisión de licencias de conducir y tarjetas de circulación.",
  "Se solicitó formalmente establecer una relación comercial directa como partner de IDP Corea; sin embargo, hasta el momento no se ha recibido una respuesta favorable. La solicitud fue canalizada inicialmente a IDP Americas y posteriormente remitida a Prosoft 2000, sin ofrecer una alternativa de atención directa.",
  "Derivado de esta cadena de intermediación y de la falta de una respuesta concreta por parte del fabricante, no se identifica actualmente la seriedad ni el nivel de atención comercial requeridos para el proyecto, por lo que IDP queda descartada como opción de proveedor directo.",
  "En Estados Unidos, IDP representa actualmente la única alternativa comercial viable; no obstante, los demás distribuidores y revendedores consultados manejan precios significativamente más altos, llegando incluso a superar los costos disponibles en México.",
];

const USA_MARKET = [
  { tipo: "Ribbon YMCKO", proveedor: "ID Wholesaler", pais: "USA", precio: 1491.03 },
  { tipo: "Ribbon YMCKO", proveedor: "AlphaCard", pais: "USA", precio: 1491.03 },
  { tipo: "Ribbon YMCKO", proveedor: "IDSecurityOnline", pais: "USA", precio: 1491.20 },
  { tipo: "Ribbon YMCKO", proveedor: "Identisys", pais: "USA", precio: 1798.08 },
  { tipo: "Laminado Clear 1 mil", proveedor: "IDSecurityOnline", pais: "USA", precio: 1491.20 },
  { tipo: "Laminado Clear 1 mil", proveedor: "AlphaCard", pais: "USA", precio: 1524.92 },
  { tipo: "Laminado Clear 1 mil", proveedor: "Identisys", pais: "USA", precio: 1796.21 },
];

function toNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const cleaned = String(value)
    .replace(/,/g, "")
    .replace(/[^0-9.-]/g, "")
    .trim();

  if (!cleaned) return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function isMeaningfulValue(value) {
  if (value === null || value === undefined) return false;
  const text = String(value).trim().toLowerCase();

  if (!text) return false;
  if (text === "no disponible") return false;
  if (text === "sin información") return false;
  if (text === "sin informacion") return false;
  if (text === "—") return false;
  if (text === "-") return false;
  if (text === "estimación en proceso") return false;
  if (text === "estimacion en proceso") return false;

  return true;
}

function formatCurrency(value, digits = 2) {
  const num = toNumber(value);
  if (num === null) return value ?? "—";

  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(num);
}

function formatMoneyWithSymbol(value, digits = 2) {
  const num = toNumber(value);
  if (num === null) return value ?? "—";

  return `$${new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(num)}`;
}

function formatPercent(value) {
  const num = toNumber(value);
  if (num === null) return "—";

  return `${num.toFixed(2)}%`;
}

function getCoverageCount(item) {
  let total = 0;

  if (toNumber(item?.pvc100k) !== null) total += 1;
  if (isMeaningfulValue(item?.impresoraIdpSmart51l)) total += 1;
  if (isMeaningfulValue(item?.laminador)) total += 1;
  if (isMeaningfulValue(item?.ribbon)) total += 1;

  return total;
}

function resolveFobDiff(item, currentItem) {
  const direct =
    toNumber(item?.diferencialFob) ??
    toNumber(item?.diferencialPvc);

  if (direct !== null) return direct;

  const currentPvc = toNumber(currentItem?.pvc100k);
  const currentProveedor = String(currentItem?.proveedor || "").trim().toLowerCase();
  const proveedor = String(item?.proveedor || "").trim().toLowerCase();
  const region = String(item?.region || "").trim().toLowerCase();

  if (proveedor && proveedor === currentProveedor) return 0;
  if (region === "proveedor actual") return 0;

  const proveedorPvc = toNumber(item?.pvc100k);

  if (currentPvc === null || proveedorPvc === null || proveedorPvc === 0) {
    return null;
  }

  // Replica la lógica visual de tu hoja:
  // ((precioProveedor - precioActual) / precioProveedor) * 100
  return ((proveedorPvc - currentPvc) / proveedorPvc) * 100;
}

function getStatusClass(status) {
  const value = String(status || "").trim().toLowerCase();

  if (value === "destacado") return "status status--featured";
  if (value === "viable") return "status status--viable";
  if (value === "en proceso") return "status status--process";
  if (value === "a revisar") return "status status--review";
  if (value === "no recomendado") return "status status--bad";
  if (value === "descartado") return "status status--discarded";

  return "status";
}

function getDataStateClass(value) {
  const count = Number(value || 0);

  if (count >= 4) return "data-state data-state--ok";
  if (count >= 2) return "data-state data-state--review";
  return "data-state data-state--pending";
}

function renderCoverage(count) {
  const safe = Math.max(0, Math.min(4, Number(count || 0)));

  return (
    <div className="coverage">
      <div className="coverage__segments">
        {[1, 2, 3, 4].map((n) => (
          <span
            key={n}
            className={`coverage__segment ${n <= safe ? "coverage__segment--on" : ""}`}
          />
        ))}
      </div>
      <strong>{safe}/4</strong>
    </div>
  );
}

function getFobDiffClass(value) {
  const num = toNumber(value);
  if (num === null) return "fob-diff";
  if (num < 0) return "fob-diff fob-diff--negative";
  if (num > 0) return "fob-diff fob-diff--positive";
  return "fob-diff fob-diff--neutral";
}

function rankByPvc(items) {
  return [...items]
    .filter((x) => toNumber(x.pvc100k) !== null)
    .sort((a, b) => (toNumber(a.pvc100k) ?? 999999) - (toNumber(b.pvc100k) ?? 999999));
}

function rankByCoverage(items) {
  return [...items].sort((a, b) => {
    const covDiff = getCoverageCount(b) - getCoverageCount(a);
    if (covDiff !== 0) return covDiff;
    return (toNumber(a.pvc100k) ?? 999999) - (toNumber(b.pvc100k) ?? 999999);
  });
}

function getEligible(items) {
  return items.filter((x) => {
    const status = String(x.estatus || "").trim().toLowerCase();
    return status === "destacado" || status === "viable";
  });
}

export default function CotizacionesV2() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [activeTab, setActiveTab] = useState("comparativo");

  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [coverage, setCoverage] = useState("ALL");
  const [onlyPendientes, setOnlyPendientes] = useState(false);
  const [onlyPorValidar, setOnlyPorValidar] = useState(false);
  const [volume, setVolume] = useState(100000);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getComparativo();
      setPayload(data);
    } catch (err) {
      setError(err?.message || "No fue posible consultar las cotizaciones.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const items = useMemo(() => {
    const list = Array.isArray(payload?.items) ? payload.items : [];
    return list.map((item, index) => ({
      ...item,
      _index: index + 1,
      coverageCount: getCoverageCount(item),
    }));
  }, [payload]);

  const currentItem = useMemo(() => {
    return (
      items.find((x) => String(x.region || "").trim().toLowerCase() === "proveedor actual") ||
      items.find((x) => String(x.proveedor || "").trim().toLowerCase() === "prosoft 2000") ||
      items[0] ||
      null
    );
  }, [items]);

  const itemsWithDiff = useMemo(() => {
    return items.map((item) => ({
      ...item,
      diferencialFobCalculado: resolveFobDiff(item, currentItem),
    }));
  }, [items, currentItem]);

  const eligibleItems = useMemo(() => getEligible(itemsWithDiff), [itemsWithDiff]);

  const regions = useMemo(() => {
    return [...new Set(itemsWithDiff.map((x) => x.region).filter(Boolean))];
  }, [itemsWithDiff]);

  const statuses = useMemo(() => {
    return [...new Set(itemsWithDiff.map((x) => x.estatus).filter(Boolean))];
  }, [itemsWithDiff]);

  const filteredItems = useMemo(() => {
    return itemsWithDiff.filter((item) => {
      const text = `${item.proveedor || ""} ${item.region || ""} ${item.ubicacion || ""}`.toLowerCase();
      const searchOk = !search || text.includes(search.toLowerCase());

      const regionOk = region === "ALL" || item.region === region;
      const statusOk = status === "ALL" || item.estatus === status;

      let coverageOk = true;
      if (coverage === "4") coverageOk = item.coverageCount === 4;
      if (coverage === "3") coverageOk = item.coverageCount === 3;
      if (coverage === "2") coverageOk = item.coverageCount <= 2;

      const pendientesOk = !onlyPendientes || item.coverageCount < 4;
      const validarOk = !onlyPorValidar || item.coverageCount < 4;

      return searchOk && regionOk && statusOk && coverageOk && pendientesOk && validarOk;
    });
  }, [itemsWithDiff, search, region, status, coverage, onlyPendientes, onlyPorValidar]);

  const lowestPvc = useMemo(() => {
    return rankByPvc(itemsWithDiff)[0] || null;
  }, [itemsWithDiff]);

  const bestEligible = useMemo(() => {
    return rankByPvc(eligibleItems)[0] || null;
  }, [eligibleItems]);

  const mostComplete = useMemo(() => {
    const candidates = itemsWithDiff.filter((x) => String(x.region || "").trim().toLowerCase() !== "proveedor actual");
    return rankByCoverage(candidates)[0] || null;
  }, [itemsWithDiff]);

  const ahorroUnitario = useMemo(() => {
    return LICENSE_SCENARIO.currentUnitCost - LICENSE_SCENARIO.alternativeUnitCost;
  }, []);

  const ahorroPorcentaje = useMemo(() => {
    if (!LICENSE_SCENARIO.alternativeUnitCost) return 0;
    return (ahorroUnitario / LICENSE_SCENARIO.currentUnitCost) * 100;
  }, [ahorroUnitario]);

  const ahorroProyectado = useMemo(() => {
    return ahorroUnitario * volume;
  }, [ahorroUnitario, volume]);

  const usaRibbon = useMemo(() => {
    return USA_MARKET.filter((x) => x.tipo.toLowerCase().includes("ribbon")).sort((a, b) => a.precio - b.precio);
  }, []);

  const usaLaminado = useMemo(() => {
    return USA_MARKET.filter((x) => x.tipo.toLowerCase().includes("laminado")).sort((a, b) => a.precio - b.precio);
  }, []);

  const updatedAt = payload?.actualizadoEn
    ? new Date(payload.actualizadoEn).toLocaleString("es-MX")
    : "Sin actualización";

  const sheetTitle = payload?.spreadsheet?.titulo || "Google Sheet";
  const sheetId = payload?.spreadsheet?.id || "";
  const googleSheetUrl = sheetId
    ? `https://docs.google.com/spreadsheets/d/${sheetId}/edit`
    : null;

  return (
    <div className="quotes-page">
      <header className="quotes-header">
        <div>
          <div className="eyebrow">IP DEPOT · COMPRAS</div>
          <h1>Comparativo de cotizaciones</h1>
          <p>Evaluación de proveedores, equipos, insumos y costo final del proyecto.</p>
        </div>

        <div className="quotes-header__actions">
          <span>Última actualización · {updatedAt}</span>
          <div>
            <button className="button button--primary" onClick={loadData}>
              ↻ Actualizar
            </button>

            {googleSheetUrl && (
              <button
                className="button"
                onClick={() => window.open(googleSheetUrl, "_blank", "noopener,noreferrer")}
              >
                ↗ Abrir Google Sheet
              </button>
            )}
          </div>
        </div>
      </header>

      <nav className="quotes-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`quotes-tab ${activeTab === tab.key ? "quotes-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <section className="metrics-bar">
        <div className="metric">
          <div className="metric__value">{itemsWithDiff.length}</div>
          <div className="metric__label">Proveedores principales</div>
        </div>

        <div className="metric">
          <div className="metric__value">{eligibleItems.length}</div>
          <div className="metric__label">Proveedores viables</div>
        </div>

        <div className="metric">
          <div className="metric__value metric__value--text">{bestEligible?.proveedor || "—"}</div>
          <div className="metric__label">Mejor PVC elegible</div>
        </div>

        <div className="metric">
          <div className="metric__value metric__value--text">{mostComplete?.proveedor || "—"}</div>
          <div className="metric__label">Proveedor más completo</div>
        </div>

        <div className="metric">
          <div className="metric__value">{formatMoneyWithSymbol(LICENSE_SCENARIO.alternativeUnitCost)}</div>
          <div className="metric__label">Costo licencia terminada</div>
        </div>

        <div className="metric">
          <div className="metric__value">{formatPercent(ahorroPorcentaje)}</div>
          <div className="metric__label">Ahorro vs proveedor actual</div>
        </div>
      </section>

      {!!error && (
        <div className="quotes-alert">
          <strong>No se pudo actualizar el comparativo.</strong>
          <span>{error}</span>
          <span>Endpoint: {COTIZACIONES_API}</span>
        </div>
      )}

      <main className="quotes-main">
        {loading ? (
          <div className="quotes-card quotes-loading">Cargando comparativo...</div>
        ) : null}

        {!loading && activeTab === "comparativo" && (
          <>
            <section className="quotes-card">
              <div className="filters">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar proveedor, región o ubicación..."
                />

                <select value={region} onChange={(e) => setRegion(e.target.value)}>
                  <option value="ALL">Todas las regiones</option>
                  {regions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>

                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="ALL">Todos los estatus</option>
                  {statuses.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>

                <select value={coverage} onChange={(e) => setCoverage(e.target.value)}>
                  <option value="ALL">Cobertura</option>
                  <option value="4">4/4 completo</option>
                  <option value="3">3/4</option>
                  <option value="2">2/4 o menor</option>
                </select>

                <button
                  className={`filter-toggle ${onlyPendientes ? "filter-toggle--active" : ""}`}
                  onClick={() => setOnlyPendientes((v) => !v)}
                >
                  Pendientes
                </button>

                <button
                  className={`filter-toggle ${onlyPorValidar ? "filter-toggle--active" : ""}`}
                  onClick={() => setOnlyPorValidar((v) => !v)}
                >
                  Por validar
                </button>

                <button
                  className="filter-clear"
                  onClick={() => {
                    setSearch("");
                    setRegion("ALL");
                    setStatus("ALL");
                    setCoverage("ALL");
                    setOnlyPendientes(false);
                    setOnlyPorValidar(false);
                  }}
                >
                  Limpiar
                </button>
              </div>

              <div className="table-count">
                {filteredItems.length} de {itemsWithDiff.length} proveedores
              </div>

              <div className="table-scroll">
                <table className="quotes-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Proveedor</th>
                      <th>Región</th>
                      <th>Ubicación</th>
                      <th>PVC</th>
                      <th>Dif. FOB</th>
                      <th>Impresora</th>
                      <th>Laminador</th>
                      <th>Ribbon</th>
                      <th>Cobertura</th>
                      <th>Estado del dato</th>
                      <th>Estatus</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredItems.map((item, idx) => {
                      const coverageCount = item.coverageCount;
                      const dataState = coverageCount >= 4 ? "Completo" : "Por validar";
                      const isCurrent = String(item.region || "").trim().toLowerCase() === "proveedor actual";

                      return (
                        <tr
                          key={`${item.proveedor}-${idx}`}
                          className={String(item.estatus || "").trim().toLowerCase() === "destacado" ? "row-featured" : ""}
                        >
                          <td>{idx + 1}</td>

                          <td>
                            <strong>{item.proveedor || "—"}</strong>
                            {isCurrent && <span className="current-provider">Proveedor actual</span>}
                          </td>

                          <td>{item.region || "—"}</td>
                          <td>{item.ubicacion || "—"}</td>

                          <td className="number">{formatMoneyWithSymbol(item.pvc100k)}</td>

                          <td className="number">
                            <span className={getFobDiffClass(item.diferencialFobCalculado)}>
                              {formatPercent(item.diferencialFobCalculado)}
                            </span>
                          </td>

                          <td className="number">{formatMoneyWithSymbol(item.impresoraIdpSmart51l)}</td>
                          <td className="number">{formatMoneyWithSymbol(item.laminador)}</td>
                          <td className="number">{formatMoneyWithSymbol(item.ribbon)}</td>

                          <td>{renderCoverage(coverageCount)}</td>

                          <td>
                            <span className={getDataStateClass(coverageCount)}>{dataState}</span>
                          </td>

                          <td>
                            <span className={getStatusClass(item.estatus)}>{item.estatus || "—"}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="table-footer">
                <span>Cobertura = PVC · Impresora · Laminador · Ribbon confirmados.</span>
                <button type="button">Ver pendientes ({itemsWithDiff.filter((x) => x.coverageCount < 4).length})</button>
                <button type="button">Validación de datos ({itemsWithDiff.length})</button>
              </div>
            </section>

            <section className="license-cost">
              <div className="section-heading">
                <div>
                  <h2>Costo por licencia terminada</h2>
                  <p>Tarjeta + ribbon + laminado. No incluye impresora ni Hologram Master.</p>
                </div>

                <div className="volume-picker">
                  <span>Volumen</span>
                  {[100000, 250000, 500000, 1000000].map((n) => (
                    <button
                      key={n}
                      className={`volume ${volume === n ? "volume--active" : ""}`}
                      onClick={() => setVolume(n)}
                    >
                      {new Intl.NumberFormat("es-MX").format(n)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="license-grid">
                <div>
                  <span className="mini-label">Proveedor actual · {LICENSE_SCENARIO.currentProvider}</span>
                  <strong>{formatMoneyWithSymbol(LICENSE_SCENARIO.currentUnitCost, 4)}</strong>
                  <small>
                    {new Intl.NumberFormat("es-MX").format(volume)} = {formatMoneyWithSymbol(LICENSE_SCENARIO.currentUnitCost * volume, 0)}
                  </small>
                </div>

                <div>
                  <span className="mini-label">Alternativa · {LICENSE_SCENARIO.alternativeProvider}</span>
                  <strong className="positive">{formatMoneyWithSymbol(LICENSE_SCENARIO.alternativeUnitCost, 2)}</strong>
                  <small>
                    {new Intl.NumberFormat("es-MX").format(volume)} = {formatMoneyWithSymbol(LICENSE_SCENARIO.alternativeUnitCost * volume, 0)}
                  </small>
                </div>

                <div>
                  <span className="mini-label">Ahorro unitario</span>
                  <strong>{formatMoneyWithSymbol(ahorroUnitario, 4)}</strong>
                  <small>{formatPercent(ahorroPorcentaje)} menos por licencia</small>
                </div>

                <div>
                  <span className="mini-label">Ahorro proyectado</span>
                  <strong className="positive">{formatMoneyWithSymbol(ahorroProyectado, 0)}</strong>
                  <small>unitario × volumen</small>
                </div>
              </div>
            </section>

            <section className="price-duel">
              <div>
                <span className="note-kicker">Precio más bajo registrado · PVC</span>
                <div className="price-duel__value">
                  <strong>{lowestPvc ? formatMoneyWithSymbol(lowestPvc.pvc100k) : "—"}</strong>
                  <span>{lowestPvc?.proveedor || "—"}</span>
                  {lowestPvc?.estatus && (
                    <span className={getStatusClass(lowestPvc.estatus)}>{lowestPvc.estatus}</span>
                  )}
                </div>
                <small>Precio absoluto, independientemente del estatus comercial.</small>
              </div>

              <div className="price-duel__eligible">
                <span className="note-kicker">Mejor precio entre elegibles · PVC</span>
                <div className="price-duel__value">
                  <strong className="positive">{bestEligible ? formatMoneyWithSymbol(bestEligible.pvc100k) : "—"}</strong>
                  <span>{bestEligible?.proveedor || "—"}</span>
                  {bestEligible?.estatus && (
                    <span className={getStatusClass(bestEligible.estatus)}>{bestEligible.estatus}</span>
                  )}
                </div>
                <small>Elegibles = Destacado o Viable.</small>
              </div>
            </section>
          </>
        )}

        {!loading && activeTab === "componentes" && (
          <section className="component-grid">
            <div className="component-card">
              <h3>Ranking PVC</h3>
              <div className="component-subtitle">Precio absoluto</div>

              {rankByPvc(itemsWithDiff).map((item, idx) => (
                <div className="rank-row" key={`pvc-${idx}`}>
                  <strong>{idx + 1}</strong>
                  <div>
                    <strong>{item.proveedor}</strong>
                    <em>{item.region}</em>
                  </div>
                  <strong>{formatMoneyWithSymbol(item.pvc100k)}</strong>
                  <span className={getStatusClass(item.estatus)}>{item.estatus}</span>
                </div>
              ))}
            </div>

            <div className="component-card">
              <h3>Ranking elegible PVC</h3>
              <div className="component-subtitle component-subtitle--eligible">Solo Destacado / Viable</div>

              {rankByPvc(eligibleItems).map((item, idx) => (
                <div className="rank-row rank-row--eligible" key={`eligible-${idx}`}>
                  <strong>{idx + 1}</strong>
                  <div>
                    <strong>{item.proveedor}</strong>
                    <em>{item.region}</em>
                  </div>
                  <strong>{formatMoneyWithSymbol(item.pvc100k)}</strong>
                </div>
              ))}
            </div>
          </section>
        )}

        {!loading && activeTab === "combinada" && (
          <section className="purchase-combined">
            <div className="section-heading section-heading--page">
              <div>
                <h2>Compra combinada</h2>
                <p>Escenario sugerido para reducir costo total y mantener cobertura útil de compra.</p>
              </div>
            </div>

            <div className="combined-warning">
              Recomendación operativa: separar compra por insumo puede bajar el costo total del proyecto.
            </div>

            <div className="combined-grid">
              <div>
                <span className="mini-label">Recomendación de decisión</span>
                <dl>
                  <dt>Mejor PVC elegible</dt>
                  <dd>{bestEligible?.proveedor || "—"}</dd>

                  <dt>Proveedor más completo</dt>
                  <dd>{mostComplete?.proveedor || "—"}</dd>

                  <dt>Proveedor actual</dt>
                  <dd>{currentItem?.proveedor || "—"}</dd>

                  <dt>Costo final referencia</dt>
                  <dd>{formatMoneyWithSymbol(LICENSE_SCENARIO.alternativeUnitCost)}</dd>
                </dl>
              </div>

              <div>
                <span className="mini-label">Criterios de lectura</span>
                <dl>
                  <dt>Completo 4/4</dt>
                  <dd>Proveedor con PVC + impresora + laminador + ribbon</dd>

                  <dt>Viable</dt>
                  <dd>Proveedor elegible para evaluación comercial</dd>

                  <dt>Destacado</dt>
                  <dd>Proveedor con ventaja comercial visible</dd>

                  <dt>Pendiente</dt>
                  <dd>Faltan componentes, validación o cierre de cotización</dd>
                </dl>
              </div>
            </div>
          </section>
        )}

        {!loading && activeTab === "usa" && (
          <section className="usa-grid">
            <div className="usa-card">
              <h3>Mercado USA · Ribbon YMCKO</h3>

              <div className="usa-stats">
                <div>
                  <span>Opciones</span>
                  <strong>{usaRibbon.length}</strong>
                </div>
                <div>
                  <span>Mejor precio</span>
                  <strong>{formatMoneyWithSymbol(usaRibbon[0]?.precio)}</strong>
                </div>
                <div>
                  <span>Proveedor líder</span>
                  <strong>{usaRibbon[0]?.proveedor || "—"}</strong>
                </div>
                <div>
                  <span>País</span>
                  <strong>USA</strong>
                </div>
              </div>

              <div className="usa-ranking">
                {usaRibbon.map((item, idx) => (
                  <div key={`usa-ribbon-${idx}`}>
                    <strong>{idx + 1}</strong>
                    <div>
                      <strong>{item.proveedor}</strong>
                      <em>{item.pais}</em>
                    </div>
                    <strong>{formatMoneyWithSymbol(item.precio)}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="usa-card">
              <h3>Mercado USA · Laminado Clear 1 mil</h3>

              <div className="usa-stats">
                <div>
                  <span>Opciones</span>
                  <strong>{usaLaminado.length}</strong>
                </div>
                <div>
                  <span>Mejor precio</span>
                  <strong>{formatMoneyWithSymbol(usaLaminado[0]?.precio)}</strong>
                </div>
                <div>
                  <span>Proveedor líder</span>
                  <strong>{usaLaminado[0]?.proveedor || "—"}</strong>
                </div>
                <div>
                  <span>País</span>
                  <strong>USA</strong>
                </div>
              </div>

              <div className="usa-ranking">
                {usaLaminado.map((item, idx) => (
                  <div key={`usa-lam-${idx}`}>
                    <strong>{idx + 1}</strong>
                    <div>
                      <strong>{item.proveedor}</strong>
                      <em>{item.pais}</em>
                    </div>
                    <strong>{formatMoneyWithSymbol(item.precio)}</strong>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {!loading && activeTab === "notas" && (
          <>
            <section className="notes-metrics">
              <div className="metric">
                <div className="metric__value">{EXEC_NOTES.length}</div>
                <div className="metric__label">Notas clave</div>
              </div>

              <div className="metric">
                <div className="metric__value">{itemsWithDiff.filter((x) => x.coverageCount < 4).length}</div>
                <div className="metric__label">Proveedores incompletos</div>
              </div>

              <div className="metric">
                <div className="metric__value">{eligibleItems.length}</div>
                <div className="metric__label">Elegibles</div>
              </div>

              <div className="metric">
                <div className="metric__value">{formatMoneyWithSymbol(42372.50)}</div>
                <div className="metric__label">Hologram Master</div>
              </div>

              <div className="metric">
                <div className="metric__value metric__value--text">{sheetTitle}</div>
                <div className="metric__label">Fuente base</div>
              </div>
            </section>

            <section className="notes-grid">
              <div className="note-card">
                <h3>Notas ejecutivas</h3>
                <ul>
                  {EXEC_NOTES.map((note, idx) => (
                    <li key={`note-${idx}`}>{note}</li>
                  ))}
                </ul>
              </div>

              <div className="note-card">
                <h3>Checklist de validación</h3>
                <ul className="check-list">
                  <li><span /> Cerrar laminador pendiente</li>
                  <li><span /> Cerrar envío/importación</li>
                  <li><span /> Confirmar IVA cuando aplique</li>
                  <li><span /> Validar compra integral vs compra por componente</li>
                  <li><span /> Confirmar recomendación final</li>
                </ul>
              </div>

              <div className="note-card note-card--wide">
                <h3>Contexto ejecutivo ampliado</h3>

                <div className="idp-flow">
                  <span>IDP Corea</span>
                  <span>→</span>
                  <span>IDP Americas</span>
                  <span>→</span>
                  <span>Prosoft 2000</span>
                </div>

                <div className="idp-status">
                  Estatus comercial: no se consolidó atención directa con fabricante.
                </div>

                <div className="idp-context">
                  {EXEC_CONTEXT.map((item, idx) => (
                    <p key={`ctx-${idx}`} style={{ margin: idx === 0 ? 0 : "10px 0 0" }}>
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}